import { PublicClientApplication } from '@azure/msal-browser';
import { useSyncExternalStore } from 'react';
import { api } from '@/api/client';
import type { Configuration, Principal, ResultatConnexion } from '@/api/types';

export interface Session {
  jeton: string;
  expireLe: string;
  utilisateur: Principal;
}

const CLE = 'f360-session';

function lire(): Session | null {
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return null;
    const s = JSON.parse(brut) as Session;
    if (!s.jeton || !s.utilisateur || new Date(s.expireLe).getTime() <= Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

let courante: Session | null = lire();
const abonnes = new Set<() => void>();

/** Session FONAREV 360 : jeton de l'API et utilisateur connecté, conservés dans le navigateur jusqu'à expiration. */
export const sessionStore = {
  get: (): Session | null => courante,
  set(s: Session | null) {
    courante = s;
    try {
      if (s) localStorage.setItem(CLE, JSON.stringify(s));
      else localStorage.removeItem(CLE);
    } catch {
      /* stockage indisponible : la session vit en mémoire */
    }
    for (const cb of abonnes) cb();
  },
  subscribe(cb: () => void) {
    abonnes.add(cb);
    return () => abonnes.delete(cb);
  },
};

export function useSession(): Session | null {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.get, sessionStore.get);
}

const ouvrir = (r: ResultatConnexion): Session => {
  const s = { jeton: r.jeton, expireLe: r.expireLe, utilisateur: r.utilisateur };
  sessionStore.set(s);
  return s;
};

export async function configuration(): Promise<Configuration> {
  return api<Configuration>('/auth/configuration', { anonyme: true });
}

export async function connexionLocale(email: string, motDePasse: string, codeTotp?: string): Promise<ResultatConnexion> {
  const r = await api<ResultatConnexion>('/auth/local', { corps: { email, motDePasse, ...(codeTotp ? { codeTotp } : {}) }, anonyme: true });
  ouvrir(r);
  return r;
}

let msal: PublicClientApplication | null = null;
const PORTEES = ['openid', 'profile', 'email'];

async function instanceMsal(cfg: Configuration): Promise<PublicClientApplication> {
  if (!cfg.microsoft || !cfg.authority) throw new Error('Connexion Microsoft 365 non configurée sur ce serveur.');
  if (!msal) {
    msal = new PublicClientApplication({
      auth: { clientId: cfg.clientId, authority: cfg.authority, redirectUri: `${location.origin}/connexion`, postLogoutRedirectUri: `${location.origin}/connexion` },
      cache: { cacheLocation: 'sessionStorage' },
    });
    await msal.initialize();
  }
  return msal;
}

/** Le navigateur revient de Microsoft avec une réponse d'authentification dans l'adresse. */
export const retourMicrosoftEnCours = (): boolean => /[#&?](code|error|id_token)=/.test(location.hash) || /[#&?]state=/.test(location.hash);

/**
 * Connexion Microsoft 365 en redirection pleine page (MSAL, code + PKCE) : pas de fenêtre surgissante, donc ni blocage
 * du navigateur ni fenêtre orpheline après l'inscription à Microsoft Authenticator. Au retour sur « /connexion »,
 * terminerRetourMicrosoft() échange le jeton d'identité contre une session FONAREV 360.
 * L'URI « {origine}/connexion » doit être déclarée dans l'inscription Entra ID (application monopage) ;
 * en développement, http://localhost:5173/connexion (Entra ID refuse 127.0.0.1).
 */
export async function connexionMicrosoft(cfg: Configuration): Promise<never> {
  if (location.hostname === '127.0.0.1') {
    location.replace(location.href.replace('127.0.0.1', 'localhost'));
    return new Promise(() => {});
  }
  const m = await instanceMsal(cfg);
  await m.loginRedirect({ scopes: PORTEES, prompt: 'select_account' });
  return new Promise(() => {});
}

export async function terminerRetourMicrosoft(cfg: Configuration): Promise<ResultatConnexion | null> {
  const m = await instanceMsal(cfg);
  const r = await m.handleRedirectPromise();
  if (!r?.idToken) return null;
  const session = await api<ResultatConnexion>('/auth/microsoft', { corps: { jeton: r.idToken }, anonyme: true });
  ouvrir(session);
  return session;
}

export async function deconnexion(): Promise<void> {
  const source = courante?.utilisateur.source;
  sessionStore.set(null);
  if (source === 'ENTRA' && msal) {
    try {
      await msal.logoutRedirect();
    } catch {
      /* déconnexion locale suffisante */
    }
  }
}
